<script lang="ts">
  import ProblemNotice from './ProblemNotice.svelte'
  import { appProblem } from './problems'
  import { t } from './i18n.svelte'
  import { account } from './account.svelte'
  import { friends, shortId } from './friends.svelte'
  import { navigate } from './router.svelte'
  import { watchSharedRoom } from './room-navigation'
  import { tone } from './social'
  import type { Friend } from './social-protocol'

  /** Requests you sent this session, shown as Pending. */
  let {
    sent = [],
    room = null,
    active = null,
  }: {
    active?: 'settings' | null
    sent?: { id: string; name: string }[]
    /** While hosting: who is watching, and how to bring more friends in. */
    room?: {
      viewers: string[]
      invited: Record<string, boolean>
      linkCopied: boolean
      onInvite: (userId: string) => void
      onSendLink: () => void
    } | null
  } = $props()

  const present = $derived(friends.sorted.filter((f) => f.presence.state !== 'offline'))
  const onSofa = $derived(room ? present.filter((f) => room.viewers.includes(f.name)) : [])
  const online = $derived(room ? present.filter((f) => !onSofa.includes(f)) : present)
  const offline = $derived(friends.sorted.filter((f) => f.presence.state === 'offline'))
  const initial = (name: string) => (name[0] ?? '?').toUpperCase()

  function presenceLabel(friend: Friend): string {
    switch (friend.presence.state) {
      case 'sharing':
        return t('Sharing now')
      case 'online':
        return t('Online')
      default:
        return t('Offline')
    }
  }
</script>

<aside class="friends-rail">
  <div class="friends-rail__head">
    <h2>{t('Friends')}</h2>
    <button
      type="button"
      class="pill-add"
      onclick={() => navigate('/friends')}
      aria-label={t('Add a friend')}>+ {t('Add')}</button
    >
  </div>
  <div class="friends-rail__list">
    {#if friends.notice}
      <ProblemNotice problem={appProblem(friends.notice, 'account')} compact />
    {:else if friends.status !== 'online'}
      <p class="muted" role="status">
        {friends.status === 'connecting' ? t('Connecting…') : t('Reconnecting…')}
      </p>
    {/if}
    {#if friends.requests.length}
      <div class="eyebrow">{t('Requests')} · {friends.requests.length}</div>
      {#each friends.requests as request (request.user_id)}
        <div class="friend-row">
          <span class="avatar avatar--sm avatar--{tone(request.name)}">{initial(request.name)}</span
          >
          <span class="friend-row__text">
            <strong>{request.name || t('Someone')}</strong>
            <span>{request.handle ? `@${request.handle}` : shortId(request.user_id)}</span>
          </span>
          <button type="button" class="link-btn" onclick={() => friends.accept(request.user_id)}
            >{t('Accept')}</button
          >
          <button
            type="button"
            class="link-btn link-btn--dim"
            aria-label={t('Decline')}
            onclick={() => friends.decline(request.user_id)}>✕</button
          >
        </div>
      {/each}
    {/if}

    {#if room && onSofa.length}
      <div class="eyebrow" style:margin-top={friends.requests.length ? '14px' : undefined}>
        {t('On the sofa')} · {onSofa.length}
      </div>
      {#each onSofa as friend (friend.user_id)}
        <div class="friend-row">
          <span class="avatar avatar--sm avatar--{tone(friend.name)} friend-row__avatar">
            {initial(friend.name)}<span class="friend-row__dot"></span>
          </span>
          <span class="friend-row__text"><strong>{friend.name}</strong></span>
          <span class="friend-row__watching">{t('Watching')}</span>
        </div>
      {/each}
    {/if}

    <div
      class="eyebrow"
      style:margin-top={friends.requests.length || onSofa.length ? '14px' : undefined}
    >
      {t('Online')} · {online.length}
    </div>
    {#each online as friend (friend.user_id)}
      {@const isSharing = friend.presence.state === 'sharing'}
      <div class="friend-row">
        <span class="avatar avatar--sm avatar--{tone(friend.name)} friend-row__avatar">
          {initial(friend.name)}<span class="friend-row__dot"></span>
        </span>
        <span class="friend-row__text">
          <strong>{friend.name || shortId(friend.user_id)}</strong>
          <span class:friend-row__live={isSharing}>{presenceLabel(friend)}</span>
        </span>
        {#if room}
          <button
            type="button"
            class="pill-add pill-add--sm"
            disabled={room.invited[friend.user_id]}
            onclick={() => room.onInvite(friend.user_id)}
            >{room.invited[friend.user_id] ? t('Invited') : t('Invite')}</button
          >
        {:else if friend.presence.state === 'sharing'}
          {@const roomTarget = friend.presence}
          <button
            type="button"
            class="link-btn"
            onclick={() => watchSharedRoom(roomTarget, friend.user_id)}>{t('Watch')}</button
          >
        {/if}
        <button
          type="button"
          class="link-btn link-btn--dim friend-row__remove"
          aria-label={t('Remove {name}', { name: friend.name })}
          onclick={() => friends.remove(friend.user_id)}>✕</button
        >
      </div>
    {:else}
      <p class="faint" style="padding: 0 8px; margin: 4px 0">
        {t('No friends yet. Add one and they will show up here.')}
      </p>
    {/each}

    {#if offline.length}
      <div class="eyebrow" style="margin-top: 14px">{t('Offline')} · {offline.length}</div>
      {#each offline as friend (friend.user_id)}
        <div class="friend-row friend-row--off">
          <span class="avatar avatar--sm">{initial(friend.name)}</span>
          <span class="friend-row__text"
            ><strong>{friend.name || shortId(friend.user_id)}</strong></span
          >
          {#if room}
            <button type="button" class="link-btn link-btn--dim" onclick={room.onSendLink}
              >{room.linkCopied ? t('Copied') : t('Send link')}</button
            >
          {/if}
          <button
            type="button"
            class="link-btn link-btn--dim friend-row__remove"
            aria-label={t('Remove {name}', { name: friend.name })}
            onclick={() => friends.remove(friend.user_id)}>✕</button
          >
        </div>
      {/each}
    {/if}

    {#if sent.length}
      <div class="eyebrow" style="margin-top: 14px">{t('Pending')} · {sent.length}</div>
      {#each sent as person (person.id)}
        <div class="friend-row friend-row--pending">
          <span class="avatar avatar--sm avatar--pending"></span>
          <span class="friend-row__text"><strong>{person.name}</strong></span>
          <span class="faint">{t('Asked')}</span>
        </div>
      {/each}
    {/if}
  </div>

  <button
    type="button"
    class="me-row"
    class:me-row--active={active === 'settings'}
    onclick={() => navigate('/settings')}
  >
    <span class="avatar avatar--sm avatar--me"
      >{initial(friends.me?.name || account.name || '?')}</span
    >
    <span class="friend-row__text">
      <strong>{friends.me?.name || account.name || t('your account')}</strong>
      <span>{friends.me?.handle ? `@${friends.me.handle}` : (account.provider ?? '')}</span>
    </span>
    <span class="faint" style="font-weight: 600">{t('Settings')}</span>
  </button>
</aside>
